import { useEffect } from 'react';

import { Banner, Box, Text } from '@betterment/samba-react';
import { SystemStyleObject } from '@betterment/samba-styled-system/types';
import { useNotifications } from '@betterment/shared-components';
import { Notification } from '@betterment/shared-components/src/features/notifications/types';

type Props = {
  css?: SystemStyleObject;
};

type NotificationBannerProps = {
  notification: Notification;
  dismissNotification: (id: string) => void;
};

function NotificationBanner({ notification, dismissNotification }: NotificationBannerProps) {
  const { id, type, message, isDismissable } = notification;

  useEffect(() => () => dismissNotification(id), [id, dismissNotification]);

  return (
    <Banner key={id} variant={type}>
      <Banner.Content>
        <Text>{message}</Text>
      </Banner.Content>
      {isDismissable && <Banner.Close onClick={() => dismissNotification(id)} />}
    </Banner>
  );
}

export function Notifications({ css: cssProp }: Props) {
  const { notifications, dismissNotification } = useNotifications();

  if (!notifications.length) {
    return null;
  }

  return (
    <Box
      css={{
        bg: 'background.surface',
        display: 'grid',
        gridTemplateColumns: 'auto minmax(min-content, 1200px) auto',
        sm: { gridColumnGap: 'space.0' },
        md: { gridColumnGap: 'space.400' },
        ...cssProp,
      }}
    >
      <Box css={{ gridColumnStart: 2, display: 'flex', flexDirection: 'column', gap: 'space.200' }}>
        {notifications.map(notification => (
          <NotificationBanner
            key={notification.id}
            notification={notification}
            dismissNotification={dismissNotification}
          />
        ))}
      </Box>
    </Box>
  );
}
